'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import CCPALink from './CCPALink';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';
import { detectRegion } from '@/lib/consent';

const footerServices = [
{ label: 'Into23 Data+', href: '/services/data-plus' },
{ label: 'Into23 Verify+', href: '/services/verify-plus' },
{ label: 'Into23 Voice+', href: '/services/voice-plus' },
{ label: 'Human Translation', href: '/services/verify-plus#human-translation' },
{ label: 'LLM Evaluation', href: '/services/llm-evaluation' }];


const footerProduct = [
{ label: 'Client Portal', href: 'https://workspace.into23.com', external: true },
      { label: 'Supplier Portal', href: 'https://suppliers.into23.com', external: true },
{ label: 'Get a Quote', href: '/get-a-quote' },
{ label: 'Resources', href: '/resources' },
{ label: 'Certifications', href: '/certifications' }];


const footerCompany = [
{ label: 'About Us', href: '/about' },
{ label: 'Case Studies', href: '/case-studies' },
{ label: 'Blog', href: '/blog' },
{ label: 'FAQ', href: '/faq' },
{ label: 'Contact', href: '/get-a-quote' }];


function FooterLink({ label, href, external }: {label: string;href: string;external?: boolean;}) {
  const content = (
    <span
      className="text-sm inline-flex items-center gap-1 group transition-colors"
      style={{ color: 'oklch(65% .01 265)' }}>
      
      {label}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
        
        <path d="M7 7h10v10"></path>
        <path d="M7 17 17 7"></path>
      </svg>
    </span>
  );
  return (
    <li>
      {external ? (
        <a href={href} target="_blank" rel="noopener noreferrer">{content}</a>
      ) : (
        <Link href={href}>{content}</Link>
      )}
    </li>);

}

export default function Footer() {
  const [showPreferences, setShowPreferences] = useState(false);
  const [region, setRegion] = useState<'gdpr' | 'ccpa' | 'none'>('none');

  useEffect(() => {
    setRegion(detectRegion());
  }, []);

  return (
    <>
      <footer
        className="relative border-t"
        style={{ borderColor: 'rgba(31, 34, 39, 0.3)', backgroundColor: 'rgb(8, 11, 18)' }}>
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16 lg:py-20">
          {/* CTA Banner */}
          <div
            className="mb-16 rounded-2xl border p-8 lg:p-10"
            style={{
              borderColor: 'rgba(43, 98, 239, 0.2)',
              background: 'linear-gradient(to right, rgba(43,98,239,0.12), rgb(8,11,18), rgb(8,11,18))'
            }}>
            
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <p
                  className="font-mono text-xs tracking-[0.24em] uppercase mb-3"
                  style={{ color: 'rgb(43, 98, 239)' }}>
                  
                  Into23
                </p>
                <h3
                  className="font-display font-bold text-2xl lg:text-3xl mb-3"
                  style={{ color: 'oklch(95% .005 265)' }}>
                  
                  Ready to Scope Your Next Multilingual Program?
                </h3>
                <p style={{ color: 'oklch(65% .01 265)' }} className="leading-relaxed">
                  Start with a custom quote or review our methodology before you engage.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/get-a-quote">
                  <span
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-semibold transition-all"
                    style={{ backgroundColor: 'rgb(43, 98, 239)' }}>
                    
                    Get a Custom Quote
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 7h10v10"></path>
                      <path d="M7 17 17 7"></path>
                    </svg>
                  </span>
                </Link>
                <Link href="/resources">
                  <span
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border font-semibold transition-all"
                    style={{
                      borderColor: 'rgba(31, 34, 39, 0.5)',
                      color: 'oklch(95% .005 265)'
                    }}>
                    
                    Download Our Methodology
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 7h10v10"></path>
                      <path d="M7 17 17 7"></path>
                    </svg>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {/* Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="mb-5">
                <Image
                  alt="Into23"
                  src="/assets/images/Into23_NewLogo-1780324533733.png"
                  width={120}
                  height={36}
                  className="h-9 w-auto object-contain" />
                
              </div>
              <p
                className="text-sm leading-relaxed max-w-sm mb-6"
                style={{ color: 'oklch(65% .01 265)' }}>
                
                The bridge between AI speed and human accuracy. Delivering trusted multilingual operations for the world&apos;s fastest-growing enterprises.
              </p>
              <div className="flex flex-col gap-3 text-sm" style={{ color: 'oklch(65% .01 265)' }}>
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgb(43, 98, 239)' }}>
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                  <span>info@into23.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgb(43, 98, 239)' }}>
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>Hong Kong · Beijing · Shanghai · Bangalore · Tokyo</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgb(43, 98, 239)' }}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
                    <path d="M2 12h20"></path>
                  </svg>
                  <span>Serving APAC &amp; Global Markets</span>
                </div>
              </div>
              {/* Social Links */}
              <div className="flex items-center gap-4 mt-5">
                <a
                  href="https://www.linkedin.com/company/into23"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Into23 on LinkedIn"
                  className="flex items-center justify-center w-9 h-9 rounded-lg border transition-colors hover:border-cobalt-glow"
                  style={{ borderColor: 'rgba(31, 34, 39, 0.4)', color: 'oklch(65% .01 265)' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                    <rect width="4" height="12" x="2" y="9"></rect>
                    <circle cx="4" cy="4" r="2"></circle>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/channel/UCLEEz-IgrCJOmMfSZUhkDtQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Into23 on YouTube"
                  className="flex items-center justify-center w-9 h-9 rounded-lg border transition-colors hover:border-cobalt-glow"
                  style={{ borderColor: 'rgba(31, 34, 39, 0.4)', color: 'oklch(65% .01 265)' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"></path>
                    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="rgb(8, 11, 18)"></polygon>
                  </svg>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4
                className="font-display font-semibold text-sm mb-4 tracking-wide uppercase"
                style={{ color: 'oklch(95% .005 265)' }}>
                
                Services
              </h4>
              <ul className="space-y-3">
                {footerServices.map((item) =>
                <FooterLink key={item.href} label={item.label} href={item.href} external={(item as any)?.external} />
                )}
              </ul>
            </div>

            {/* Product */}
            <div>
              <h4
                className="font-display font-semibold text-sm mb-4 tracking-wide uppercase"
                style={{ color: 'oklch(95% .005 265)' }}>
                
                Product
              </h4>
              <ul className="space-y-3">
                {footerProduct.map((item) =>
                <FooterLink key={item.href} label={item.label} href={item.href} external={(item as any)?.external} />
                )}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4
                className="font-display font-semibold text-sm mb-4 tracking-wide uppercase"
                style={{ color: 'oklch(95% .005 265)' }}>
                
                Company
              </h4>
              <ul className="space-y-3">
                {footerCompany.map((item) =>
                <FooterLink key={item.href} label={item.label} href={item.href} external={(item as any)?.external} />
                )}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div
            className="mt-16 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{ borderColor: 'rgba(31, 34, 39, 0.2)' }}>
            
            <p className="text-xs" style={{ color: 'oklch(65% .01 265)' }}>
              &copy; 2026 Into23 Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-xs flex-wrap" style={{ color: 'oklch(65% .01 265)' }}>
              <Link href="/privacy-policy">
                <span className="hover:text-cobalt-glow transition-colors cursor-pointer">Privacy Policy</span>
              </Link>
              <Link href="/cookies">
                <span className="hover:text-cobalt-glow transition-colors cursor-pointer">Cookie Policy</span>
              </Link>
              <Link href="/accessibility">
                <span className="hover:text-cobalt-glow transition-colors cursor-pointer">Accessibility</span>
              </Link>
              <CCPALink />
              <button
                onClick={() => setShowPreferences(true)}
                className="hover:text-cobalt-glow transition-colors cursor-pointer"
              >
                Cookie Preferences
              </button>
              <Link href="/terms-of-service">
                <span className="hover:text-cobalt-glow transition-colors cursor-pointer">Terms of Service</span>
              </Link>
              <Link href="/certifications">
                <span className="hover:text-cobalt-glow transition-colors cursor-pointer">ISO 9001 &amp; 17100</span>
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {showPreferences && (
        <ConsentPreferenceCenter
          region={region}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </>
  );

}